import React, { useCallback, useMemo } from "react";
import Search from "@dtci/spellbook/Search";
import Button from "@dtci/spellbook/Button";
import PopoverButton from "@dtci/spellbook/PopoverButton";
import MenuList from "@dtci/spellbook/MenuList";
import ContentBlock from "@dtci/spellbook/ContentBlock";
import { usePPTState } from "tap/store/ppt/usePPT";
import { PPTPromosStatTiles } from "../components/PPTPromosGrid/PPTPromosStatTiles";
import { PPTSplitButton } from "../components/PPTSplitButton";
import styles from "./pptHomePage.module.scss";

const VIEW_OPTION_CLEAR_ALL = "clear-all";
const VIEW_OPTION_RESET_COLUMNS = "reset-columns";
const VIEW_OPTION_SAVE_DEFAULT_FILTERS = "save-default-filters";

/**
 * Search and filter controls for the PPT home page.
 * Follows CLAUDE.md convention: uses Redux selectors directly instead of receiving
 * state as props, reducing prop drilling and making the component more self-contained.
 */
export const PPTHomeSearchHeader = ({
  gridRef,
  onSearchChange,
  onResetFilters,
  onClearAllFilters,
  onSaveDefaultFilters,
  onResetColumns,
  canResetColumns,
}) => {
  // Read from Redux directly (per CLAUDE.md guidelines)
  const { promosSearch } = usePPTState();

  // Handle filter button click locally since we have gridRef
  const onFilterButtonClick = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    api.isToolPanelShowing("filters")
      ? api.closeToolPanel("filters")
      : api.openToolPanel("filters");
  }, [gridRef]);
  const viewOptions = useMemo(
    () => [
      {
        value: VIEW_OPTION_CLEAR_ALL,
        label: "Clear all filters",
      },
      {
        value: VIEW_OPTION_SAVE_DEFAULT_FILTERS,
        label: "Save filters as default",
      },
      {
        value: VIEW_OPTION_RESET_COLUMNS,
        label: "Reset column layout",
        isDisabled: !canResetColumns,
      },
    ],
    [canResetColumns],
  );

  const handleViewOptionSelect = useCallback(
    (option) => {
      switch (option?.value) {
        case VIEW_OPTION_CLEAR_ALL:
          onClearAllFilters();
          break;
        case VIEW_OPTION_SAVE_DEFAULT_FILTERS:
          onSaveDefaultFilters();
          break;
        case VIEW_OPTION_RESET_COLUMNS:
          if (canResetColumns) onResetColumns();
          break;
        default:
          break;
      }
    },
    [canResetColumns, onClearAllFilters, onResetColumns, onSaveDefaultFilters],
  );

  return (
    <ContentBlock>
      <div className={styles.promosSearchHeader}>
        <div className={styles.promosSearch}>
          <Search
            value={promosSearch}
            labelText="Search promos"
            onChange={onSearchChange}
            width="auto"
          />
        </div>
        <div className={styles.filterButtons}>
          <PPTPromosStatTiles />
          <Button
            styleType="standard"
            iconPlacement="before"
            iconType="fa-filter"
            onClick={onFilterButtonClick}
          >
            Filters
          </Button>
          <PPTSplitButton
            primaryButton={
              <Button
                styleType="standard"
                iconPlacement="before"
                iconType="fa-rotate-left"
                onClick={onResetFilters}
              >
                Reset Filters
              </Button>
            }
            dropdownButton={
              <PopoverButton
                buttonIcon="fa-chevron-down"
                buttonProps={{
                  styleType: "standard",
                  sizeType: "medium",
                  iconOnly: true,
                  title: "More view options",
                }}
                testMetadata={{
                  button: { "data-testid": "promos-view-options-toggle" },
                }}
              >
                <MenuList
                  options={viewOptions}
                  onChange={handleViewOptionSelect}
                  isStandalone
                />
              </PopoverButton>
            }
          />
        </div>
      </div>
    </ContentBlock>
  );
};
